import { useState, useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import { actionTypes } from "../../services/downloaduploadOrbits/actionTypes";
import { setUploadFileDelete } from "../../services/downloaduploadOrbits/actions";

export type UploadStatus = "STAGED" | "UPLOADING" | "DONE" | "ERROR";

export interface UploadFile {
  id: string;
  file: File;
  size: number;
  progress: number;
  status: UploadStatus;
  operator: string;
}

const isUploadInProgress = (status?: string) => status === "PENDING";

export const useUploadQueue = () => {
  const dispatch = useDispatch();

  // Local state for files not yet uploaded (staged only)
  const [stagedFiles, setStagedFiles] = useState<UploadFile[]>([]);

  // Redux state for uploaded files
  const uploadedFilesStore = useSelector((state: any) => state.uploadedOrbits);

  const addFiles = useCallback(
    (fileList: FileList, operator: string) => {
      const newFiles = Array.from(fileList)
        .filter(
          (f) =>
            !stagedFiles.some(
              (existing) =>
                existing.file.name === f.name && existing.operator === operator
            ) &&
            !Object.values(uploadedFilesStore || {}).some(
              (uploaded: any) =>
                uploaded.filename === f.name &&
                uploaded.operator === operator &&
                isUploadInProgress(uploaded.status)
            )
        )
        .map((file) => {
          return {
            id: uuidv4(),
            file,
            size: file.size,
            progress: 0,
            status: "STAGED" as const,
            operator,
          };
        });
      setStagedFiles((prev) => [...newFiles, ...prev]);
    },
    [stagedFiles, uploadedFilesStore]
  );

  const removeFile = useCallback((id: string) => {
    setStagedFiles((prev) => prev.filter((f) => f.id !== id));
    dispatch(setUploadFileDelete({ id }));
  }, [dispatch]);

  const uploadFile = useCallback(
    (file: UploadFile) => {
      dispatch({
        type: actionTypes.UPLOAD,
        payload: {
          operator: file.operator,
          files: [{ file: file.file, id: file.id, size: file.size }],
        },
      });
      // Remove from stagedFiles, Redux will now track it
      setStagedFiles((prev) => prev.filter((f) => f.id !== file.id));
    },
    [dispatch]
  );

  const uploadAll = useCallback(() => {
    stagedFiles.forEach((f) => uploadFile(f));
  }, [stagedFiles, uploadFile]);

  const reset = useCallback(() => {
    dispatch(setUploadFileDelete());
    setStagedFiles([]);
  }, [dispatch]);

  // Merge stagedFiles and Redux-tracked files for UI
  const files: UploadFile[] = useMemo(() => {
    // Redux-tracked files
    const reduxFiles: UploadFile[] = Object.entries(
      uploadedFilesStore || {}
    ).map(([id, value]: [string, any]) => {
      let status: UploadStatus = "UPLOADING";
      let progress = 50;
      if (value.status === "READY") {
        status = "DONE";
        progress = 100;
      } else if (value.status === "ERROR") {
        status = "ERROR";
        progress = 100;
      } else if (value.status === "PENDING") {
        status = "UPLOADING";
        progress = 50;
      }
      return {
        id,
        file: { name: value.filename, size: value.size },
        size: value.size,
        progress,
        status,
        operator: value.operator,
      };
    });
    const merged = [
      ...stagedFiles,
      ...reduxFiles.filter((rf) => !stagedFiles.some((sf) => sf.id === rf.id)),
    ];
    return merged;
  }, [stagedFiles, uploadedFilesStore]);

  return {
    files,
    addFiles,
    removeFile,
    uploadFile,
    uploadAll,
    reset,
  };
};
